'use client';
import React, { useState, useEffect } from 'react';
import Navigation from '@/components/Navigation';
import Hero from '@/components/Hero';
import Footer from '@/components/Footer';
import Link from 'next/link';
import { fetchAllPaddles, buildCategories_list, type CategoryInfo } from '@/lib/services/paddleService';

/* ─── Per-category SVG icons ─────────────────────────────────────────────────── */
const CategoryIcon = ({ slug }: { slug: string }) => {
  const shared = { width: 24, height: 24, fill: 'none', stroke: 'currentColor', strokeWidth: 1.75, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };

  switch (slug) {
    case 'power':
      return (
        <svg {...shared} viewBox="0 0 24 24">
          <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
        </svg>
      );
    case 'control':
      return (
        <svg {...shared} viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="3" />
          <path d="M12 2v3M12 19v3M4.22 4.22l2.12 2.12M17.66 17.66l2.12 2.12M2 12h3M19 12h3M4.22 19.78l2.12-2.12M17.66 6.34l2.12-2.12" />
        </svg>
      );
    case 'all-court':
      return (
        <svg {...shared} viewBox="0 0 24 24">
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <path d="M3 9h18M3 15h18M9 3v18M15 3v18" />
        </svg>
      );
    case 'tournament':
      return (
        <svg {...shared} viewBox="0 0 24 24">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      );
    case 'elongated':
      return (
        <svg {...shared} viewBox="0 0 24 24">
          <rect x="9" y="2" width="6" height="20" rx="3" />
          <path d="M6 8h12M6 16h12" />
        </svg>
      );
    case 'widebody':
      return (
        <svg {...shared} viewBox="0 0 24 24">
          <rect x="3" y="5" width="18" height="14" rx="4" />
          <path d="M3 12h18M8 5v14M16 5v14" />
        </svg>
      );
    default:
      return (
        <svg {...shared} viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8v4l3 3" />
        </svg>
      );
  }
};

/* ─── Per-category eyebrow labels ────────────────────────────────────────────── */
const categoryEyebrow: Record<string, string> = {
  power:      'FIREPOWER',
  control:    'PRECISION',
  'all-court':'VERSATILITY',
  tournament: 'TOURNAMENT',
  elongated:  'REACH',
  widebody:   'SWEET SPOT',
};

export default function HomePage() {
  const [categories, setCategories] = useState<CategoryInfo[]>([]);
  const [totalPaddles, setTotalPaddles] = useState(0);

  useEffect(() => {
    fetchAllPaddles().then(paddles => {
      setTotalPaddles(paddles.length);
      setCategories(buildCategories_list(paddles));
    });
  }, []);

  return (
    <div className="min-h-screen" style={{ background: '#EBEBEB' }}>
      <Navigation transparent />
      <Hero />

      {/* Stats section */}
      <section style={{ background: '#FFFFFF', padding: 'clamp(5rem, 8vw, 7.5rem) 1rem' }}>
        <div className="max-w-container mx-auto">
          <div className="flex flex-col lg:flex-row items-start gap-20">
            <div className="flex-1 max-w-lg">
              <div className="premium-chip accent mb-7">
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#D90012', display: 'inline-block', boxShadow: '0 0 6px rgba(217,0,18,0.5)' }} />
                The Platform
              </div>
              <h2
                className="font-black mb-6"
                style={{
                  fontSize: 'clamp(30px, 3.6vw, 44px)',
                  letterSpacing: '-0.05em',
                  color: '#0A0A0A',
                  lineHeight: 1.04,
                }}
              >
                {totalPaddles > 0 ? `${totalPaddles} paddles.` : '451 paddles.'}<br />
                <span style={{ color: 'rgba(10,10,10,0.26)', fontWeight: 400, fontSize: '0.86em' }}>One objective source.</span>
              </h2>
              <p
                className="mb-10"
                style={{ color: 'rgba(10,10,10,0.46)', fontSize: '15px', lineHeight: 1.82 }}
              >
                We are an impartial team of pickleball enthusiasts. Our sole mission is to help you make informed decisions
                and find the perfect paddle for your game — backed by real data, not sponsorships.
              </p>
              <div className="flex flex-wrap items-center gap-5">
                <Link href="/categories" className="versusButton pill">Browse all paddles</Link>
                <Link
                  href="/top-paddles"
                  className="text-sm font-bold transition-colors flex items-center gap-1.5"
                  style={{ color: 'rgba(10,10,10,0.36)', letterSpacing: '-0.01em', textDecoration: 'none', transitionDuration: '0.2s' }}
                  onMouseEnter={e => ((e.currentTarget as HTMLElement).style.color = '#0A0A0A')}
                  onMouseLeave={e => ((e.currentTarget as HTMLElement).style.color = 'rgba(10,10,10,0.36)')}
                >
                  How we score <span style={{ marginLeft: 2 }}>→</span>
                </Link>
              </div>
            </div>

            {/* Stats */}
            <div className="flex gap-16 lg:gap-28 flex-shrink-0">
              {[
                { num: totalPaddles > 0 ? `${totalPaddles}` : '451', label: 'Paddles' },
                { num: '57+', label: 'Brands' },
                { num: '6', label: 'Categories' },
              ].map(stat => (
                <div key={stat.label} className="text-center">
                  <div className="stat-value">{stat.num}</div>
                  <div className="stat-label">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Thin divider */}
      <div className="max-w-container mx-auto px-4">
        <div className="premium-divider" />
      </div>

      {/* ─── Categories section ──────────────────────────────────────────────────── */}
      <section style={{ background: '#FFFFFF', padding: 'clamp(4.5rem, 7vw, 6.5rem) 1rem' }}>
        <div className="max-w-container mx-auto">

          {/* Section header */}
          <div className="flex items-end justify-between mb-12">
            <div>
              <div className="premium-chip accent mb-5">Browse</div>
              <h2
                className="font-black"
                style={{
                  fontSize: 'clamp(26px, 3.2vw, 40px)',
                  letterSpacing: '-0.045em',
                  color: '#0A0A0A',
                  lineHeight: 1.04,
                }}
              >
                Shop by category
              </h2>
            </div>
            <Link
              href="/categories"
              className="text-xs font-bold uppercase tracking-widest transition-colors hidden md:flex items-center gap-1.5"
              style={{ color: 'rgba(10,10,10,0.36)', letterSpacing: '0.12em', textDecoration: 'none', transitionDuration: '0.2s' }}
              onMouseEnter={e => ((e.currentTarget as HTMLElement).style.color = '#0A0A0A')}
              onMouseLeave={e => ((e.currentTarget as HTMLElement).style.color = 'rgba(10,10,10,0.36)')}
            >
              All categories
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 6h8M6 2l4 4-4 4" />
              </svg>
            </Link>
          </div>

          {/* Card grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {categories.map(cat => (
              <Link
                key={cat.slug}
                href={`/categories/${cat.slug}`}
                className="category-card group"
              >
                {/* Top row: icon tile + count pill */}
                <div className="category-card-icon-wrap">
                  <div className="category-card-icon-bg">
                    <span className="category-hero-icon">
                      <CategoryIcon slug={cat.slug} />
                    </span>
                  </div>
                  <span className="category-card-count-pill">
                    {cat.count} paddles
                  </span>
                </div>

                {/* Body: eyebrow + title + description + footer */}
                <div className="category-card-body">
                  {categoryEyebrow[cat.slug] && (
                    <span style={{
                      display: 'block',
                      fontSize: '9.5px',
                      fontWeight: 800,
                      letterSpacing: '0.14em',
                      textTransform: 'uppercase',
                      color: 'rgba(10,10,10,0.32)',
                      marginBottom: '6px',
                    }}>
                      {categoryEyebrow[cat.slug]}
                    </span>
                  )}
                  <h3 className="category-card-title">{cat.name}</h3>
                  <p className="category-card-desc">{cat.description}</p>

                  {/* Footer row */}
                  <div className="category-card-footer">
                    <span className="category-card-count-label">{cat.count} paddles</span>
                    <span className="category-card-cta">
                      Explore
                      <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'transform 0.22s cubic-bezier(0.4,0,0.2,1)' }}>
                        <path d="M2 6.5h9M7 2.5l4 4-4 4" />
                      </svg>
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Thin divider */}
      <div className="max-w-container mx-auto px-4">
        <div className="premium-divider" />
      </div>

      {/* CTA section */}
      <section
        className="relative overflow-hidden"
        style={{ background: '#080808', padding: 'clamp(5rem, 9vw, 8rem) 1rem' }}
      >
        {/* Burgundy glow */}
        <div
          className="absolute pointer-events-none"
          style={{
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '900px',
            height: '500px',
            background: 'radial-gradient(ellipse, rgba(217,0,18,0.12) 0%, transparent 65%)',
            borderRadius: '50%',
          }}
        />
        <div className="max-w-container mx-auto relative z-10 text-center">
          <div className="premium-chip mb-8" style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.1)' }}>
            Compare
          </div>
          <h2
            className="font-black mb-6"
            style={{
              fontSize: 'clamp(28px, 4vw, 52px)',
              letterSpacing: '-0.05em',
              color: '#FFFFFF',
              lineHeight: 1.04,
            }}
          >
            Find your perfect paddle.<br />
            <span style={{ color: 'rgba(255,255,255,0.3)', fontWeight: 400, fontSize: '0.82em' }}>Side-by-side comparison.</span>
          </h2>
          <p className="mb-10" style={{ color: 'rgba(255,255,255,0.38)', fontSize: '15px', lineHeight: 1.8, maxWidth: 480, margin: '0 auto 2.5rem' }}>
            Compare any two paddles across 20+ data points — power, spin, weight, grip, and more.
          </p>
          <Link href="/compare" className="versusButton pill" style={{ background: '#D90012', color: '#fff', border: 'none' }}>
            Start comparing
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
